<style></style>

<template>
    <div class="container">
        <div class="row">
            <div class="col-sm">
                <h2>Are your programs already using Go NAPSACC?</h2>
                <p>
                    To connect with a program, check the box under the "Connect" column and then click the orange "Send Connection Invites" button. Registered programs in your region are listed below. Sort by program name, type or county - or just start typing in the blue search box.
                </p>
            </div>
        </div>
        <div class="row">
            <form action="connections/request-to-connect" method="POST" style="width:100%">
                <input type="hidden" name="_token" :value="token">
                <div class="table-template">
                    <div class="form-group d-md-none not-generic-form">
                        <div class="input-group">
                            <select v-model="sortBy">
                                <option :value="null">-- Filter --</option>
                                <option v-for="option in sortFields" :value="option.value">{{ option.text }}</option>
                            </select>
                            <select :disabled="!sortBy" v-model="sortDesc">
                                <option :value="false">Asc</option>
                                <option :value="true">Desc</option>
                            </select>
                        </div>

                        <div class="border-search">
                            <div class="input-group">
                                <input type="text" class="form-control" v-model="searchString" placeholder="Search by County or Program Name" />
                                <div class="input-group-append">
                                    <i class="fa fa-search my-auto"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row rounded-top find-program">
                        <div class="arrow-find-program">
                            <img src="~public@/images/orange_arrow_blue_border.png">
                            <span class="table-title">&nbsp;Find a Registered Program</span>
                        </div>
                        <div class="border-search d-none d-md-block">
                            <div class="input-group">
                                <input type="text" class="form-control" v-model="searchString" @input="resetPage" placeholder="Search by County or Program Name" />
                                <div class="input-group-append">
                                    <i class="fa fa-search my-auto"></i>
                                </div>
                            </div>
                        </div>
                    </div> 

                    <table class="table mb-0">
                        <thead>
                            <tr>
                                <th v-for="field in fields">{{ field.label }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in paginatedItems">
                                <td>{{ item.status }}</td>
                                <td>{{ item.name }}</td>
                                <td>{{ item.first_name }}</td>
                                <td>{{ item.last_name }}</td>
                                <td>{{ item.partner }}</td>
                                <td>{{ item.county }}</td>
                                <td>{{ item.state }}</td>
                                <td>
                                    <div class="form-check" v-if="item.status == 'Not Connected'">
                                        <input type="checkbox" class="form-check-input" :id="item.id" :name="item.id">
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="row pagination-row d-flex flex-row justify-content-between">
                        <nav aria-label="Page navigation for list of Connection Invites">
                            <ul class="pagination mt-3 mx-3" role="menubar">
                                <li class="page-item" :class="{'disabled': isOnFirstPage}">
                                    <button class="page-link" @click.prevent="prevPage" aria-label="Go to previous page">&lt;</button>
                                </li>
                                <!-- show 'first page' button and ellipsis -->
                                <template v-if="startDisplayPage > 1">
                                    <li class="page-item">
                                        <button class="page-link" @click.prevent="resetPage">1</button>
                                    </li>
                                    <li class="page-item disabled">
                                        <button class="page-link" @click.stop>&hellip;</button>
                                    </li>
                                </template>
                                <li v-for="n in pageCountToDisplay" class="page-item" :class="{'active': startDisplayPage + (n-1) == currentPage}" :aria-label="`Go to page ${startDisplayPage + (n-1)}`">
                                    <button class="page-link" @click.prevent="setPage(startDisplayPage + (n-1))">{{ startDisplayPage + (n-1) }}</button>
                                </li>
                                <!-- show 'last page' button and ellipsis -->
                                <template v-if="endDisplayPage < pageCount">
                                    <li class="page-item disabled">
                                        <button class="page-link">&hellip;</button>
                                    </li>
                                    <li class="page-item">
                                        <button class="page-link" @click.prevent="goToLastPage">{{ pageCount }}</button>
                                    </li>
                                </template>
                                <li class="page-item" :class="{'disabled': isOnLastPage}">
                                    <button class="page-link" @click.prevent="nextPage" aria-label="Go to next page">&gt;</button>
                                </li>
                            </ul>
                        </nav>
                        <div class="connect-button-div">
                            <button type="submit" class="btn btn-orange-blue">Send Connection Invites</button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            token: {
                required: true,
                type: String
            }
        },
        data() {
            return {
                programs: [],
                filter: null,
                fields: [
                    {
                        key: 'status',
                        label: 'Status',
                        sortable: true
                    },
                    {
                        key: 'name',
                        label: 'Name',
                        sortable: true
                    },
                    {
                        key: 'first_name',
                        label: 'First name',
                        sortable: true
                    },
                    {
                        key: 'last_name',
                        label: 'Last Name',
                        sortable: true
                    },
                    {
                        key: 'partner',
                        label: 'Partner',
                        sortable: true
                    },
                    {
                        key: 'county',
                        label: 'County',
                        sortable: true
                    },
                    {
                        key: 'state',
                        label: 'State',
                        sortable: true,
                        'class': 'state-col'
                    },
                    {
                        key: 'action',
                        label: 'Connect',
                        sortable: false,
                        'class': 'action-col'
                    },
                ],
                currentPage: 1,
                perPage: 5,
                maxPagesToDisplay: 10,
                sortBy: null,
                sortDesc: false,
                loading: false,
                searchString: "",
            }
        },
        methods: {
            onFiltered (filteredItems) {
                // Trigger pagination to update the number of buttons/pages due to filtering
                this.totalRows = filteredItems.length
                this.currentPage = 1
            },
            setPage: function(page) {
                console.log("Page num: " + page)
                this.currentPage = page;
            },
            nextPage: function() {
                if(this.currentPage >= this.pageCount) {
                    this.currentPage = this.pageCount;
                } else {
                    this.currentPage++;
                }
            },
            prevPage: function() {
                if(this.currentPage <= 1) {
                    this.currentPage = 1;
                } else {
                    this.currentPage--;
                }
            },
            resetPage: function() {
                this.setPage(1);
            },
            goToLastPage: function() {
                this.setPage(this.pageCount);
            }
        },
        computed: {
            sortFields: function () {
                if (this.fields) {
                    return this.fields
                        .filter(f => f.sortable)
                        .map(f => { return { text: f.label, value: f.key } })
                }
                return new Array();
            },
            pageCount: function() {
                return Math.ceil(this.filteredBySearchString.length/this.perPage);
            },
            pageCountToDisplay: function() {
                return this.pageCount > this.maxPagesToDisplay ? this.maxPagesToDisplay : this.pageCount;
            },
            paginatedItems: function() {
                let start = (this.currentPage-1) * this.perPage;
                let paginatedItems = this.programs;

                // check for string input
                if(this.searchString) { // todo: is this check redundant to check in filteredBySearchString?
                    paginatedItems = this.filteredBySearchString;
                }

                return paginatedItems.slice(start, start + this.perPage);
            },
            filteredBySearchString: function() {
                if(this.searchString) {
                    return this.programs.filter((item) => {
                        let searchableItem = {
                            ...item
                        };
                        return JSON.stringify(searchableItem).search(new RegExp(this.searchString, "gi")) >= 0;
                    });
                } else {
                    return this.programs;
                }
            },
            isOnFirstPage: function() {
                return this.currentPage == 1;
            },
            isOnLastPage: function() {
                return this.currentPage == this.pageCount;
            },
            startDisplayPage: function() {
                // set start display page so that current page is approx middle of page list
                let startPage = this.currentPage - Math.ceil(this.maxPagesToDisplay/2);
                let endPage = startPage + (this.maxPagesToDisplay - 1);
                
                if(endPage > this.pageCount) {
                    startPage -= (endPage - this.pageCount); 
                }
                
                if(startPage < 1) {
                    startPage = 1;
                }

                return startPage;
            },
            endDisplayPage: function() {
                return this.startDisplayPage + this.maxPagesToDisplay - 1;
            }
        },
        mounted: function(){
          this.loading = true;
          axios.get('/api/consultants/programs')
            .then(response => {
                    this.programs = response.data.data;
                    return response;
                })
            .catch(error => {
                return error;
            })
            .then(() => {
                this.loading = false;
            })
        }
    }
</script>